import { useEffect, useState, useCallback } from "react";
import { Col, Container, Row } from "react-bootstrap";
import styled from "styled-components";
import { Switch, FormControlLabel } from "@mui/material";

import Layout from "../components/layout/layout";
import { H1, P3 } from "../components/theme/text/Headings";
import { Button } from "../components/theme/Button";
import {
  DarkGrey,
  NeonGreen,
  White,
  Black,
  White08,
} from "../components/theme/colors";

import { useAuth } from "../services/AuthContext";
import { db } from "../services/firebase.config";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { Notifications, Settings } from "@mui/icons-material";
import PushService from "../services/PushService";
import { App as CapacitorApp } from "@capacitor/app";

const Card = styled.div`
  padding: 10px 20px;
  background-color: ${DarkGrey};
  font-weight: 500;
  color: ${(props) => props.color || White};
  font-size: 15px;
  border-radius: 10px;
  margin-bottom: 20px;
`;

const RowItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);

  &:last-child {
    border-bottom: none;
  }
`;

const SettingsNotifications = () => {
  const { currentUserState } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [osPermission, setOsPermission] = useState("prompt"); // 'granted' | 'denied' | 'prompt'
  const [working, setWorking] = useState(false);

  const [prefs, setPrefs] = useState({
    quizCompletions: true,
    friendRequests: true,
  });

  const storageKey = currentUserState
    ? `notif-prefs-${currentUserState.uid}`
    : null;

  const loadPrefs = useCallback(async () => {
    if (!currentUserState) return;
    setLoading(true);

    try {
      // Try localStorage first for fast paint
      if (storageKey) {
        const stored = localStorage.getItem(storageKey);
        if (stored) {
          try {
            const parsed = JSON.parse(stored);
            setPrefs((prev) => ({ ...prev, ...parsed }));
          } catch (_) {
            // Silently fail - localStorage parse errors are non-critical
          }
        }
      }

      // Load from Firestore
      const settingsRef = doc(db, "user-settings", currentUserState.uid);
      const snap = await getDoc(settingsRef);
      if (snap.exists()) {
        const data = snap.data();
        const next = {
          quizCompletions: data?.notifications?.quizCompletions ?? true,
          friendRequests: data?.notifications?.friendRequests ?? true,
        };
        setPrefs(next);
        if (storageKey) {
          localStorage.setItem(storageKey, JSON.stringify(next));
        }
      } else {
        // Seed defaults in Firestore
        const defaults = {
          notifications: {
            quizCompletions: true,
            friendRequests: true,
          },
        };
        await setDoc(settingsRef, defaults, { merge: true });
        if (storageKey) {
          localStorage.setItem(
            storageKey,
            JSON.stringify(defaults.notifications)
          );
        }
      }
    } catch (e) {
      console.warn("Failed to load notification prefs:", e);
    } finally {
      setLoading(false);
    }
  }, [currentUserState, storageKey]);

  useEffect(() => {
    loadPrefs();
  }, [loadPrefs]);

  // Load OS permission and, if granted, ensure registration/upsert occurs
  const refreshOsPermission = useCallback(async () => {
    try {
      const status = await PushService.checkPermissionStatus();
      setOsPermission(status || "prompt");
      if (status === "granted" && currentUserState) {
        await PushService.initializeIfPermitted(currentUserState);
      }
    } catch (_) {
      setOsPermission("denied");
    }
  }, [currentUserState]);

  useEffect(() => {
    refreshOsPermission();
  }, [refreshOsPermission]);

  const handleEnablePush = async () => {
    if (!currentUserState) return;
    setWorking(true);
    try {
      const granted = await PushService.requestAndRegister(currentUserState);
      setOsPermission(granted ? "granted" : "denied");
    } finally {
      setWorking(false);
    }
  };

  const openSystemSettings = async () => {
    try {
      await CapacitorApp.openUrl({ url: "app-settings:" });
    } catch (e) {
      console.warn("Unable to open system settings:", e);
    }
  };

  const updatePrefs = async (partial) => {
    if (!currentUserState) return;
    const next = { ...prefs, ...partial };
    setPrefs(next);
    setSaving(true);
    try {
      const settingsRef = doc(db, "user-settings", currentUserState.uid);
      await setDoc(
        settingsRef,
        {
          notifications: {
            quizCompletions: next.quizCompletions,
            friendRequests: next.friendRequests,
          },
          updatedAt: new Date().toISOString(),
        },
        { merge: true }
      );
      if (storageKey) {
        localStorage.setItem(storageKey, JSON.stringify(next));
      }
    } catch (e) {
      console.error("Failed to save notification prefs:", e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout $background={Black}>
      <Container>
        <Row className="justify-content-center">
          <Col style={{ padding: "0px 30px" }}>
            <H1 className="text-align-center mt-5-real mb-30" color={White}>
              Notifications
            </H1>
            <P3 className="mt-10 mb-15" color={White08}>
              <Settings className="mr-5" />
              System status
            </P3>
            <Card>
              <RowItem>
                <span style={{ color: White08, fontSize: 15 }}>
                  Push permission status
                </span>
                <span style={{ color: White08, fontSize: 15, opacity: 0.8 }}>
                  {osPermission}
                </span>
              </RowItem>
              {osPermission === "prompt" && (
                <RowItem>
                  <Button
                    onClick={handleEnablePush}
                    disabled={working}
                    $background={DarkGrey}
                    width="100%"
                    style={{ fontSize: "15px" }}
                  >
                    {working ? "Enabling..." : "Enable Push Notifications"}
                  </Button>
                </RowItem>
              )}
              {osPermission === "denied" && (
                <>
                  <RowItem>
                    <div style={{ fontSize: 14, color: White08 }}>
                      Push is disabled in your system settings. Open settings to
                      enable, then tap Refresh.
                    </div>
                  </RowItem>
                  <RowItem>
                    <Button onClick={openSystemSettings}>Open Settings</Button>
                    <Button onClick={refreshOsPermission}>Refresh</Button>
                  </RowItem>
                </>
              )}
              {osPermission === "granted" && (
                <RowItem>
                  <div style={{ fontSize: 14, color: White08 }}>
                    Push is enabled. You can fine-tune categories below.
                  </div>
                </RowItem>
              )}
            </Card>

            <P3 className="mt-30 mb-15" color={White08}>
              <Notifications className="mr-5" />
              Set notification preferences
            </P3>
            <Card>
              <RowItem>
                <span style={{ color: White08, fontSize: 15 }}>
                  Quiz completions
                </span>
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!prefs.quizCompletions}
                      onChange={(e) =>
                        updatePrefs({ quizCompletions: e.target.checked })
                      }
                      sx={{
                        "& .MuiSwitch-switchBase.Mui-checked": {
                          color: White,
                        },
                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                          {
                            backgroundColor: NeonGreen,
                            opacity: 1,
                          },
                      }}
                    />
                  }
                  label=""
                />
              </RowItem>

              <RowItem>
                <span style={{ color: White08, fontSize: 15 }}>
                  Friend requests
                </span>
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!prefs.friendRequests}
                      onChange={(e) =>
                        updatePrefs({ friendRequests: e.target.checked })
                      }
                      sx={{
                        "& .MuiSwitch-switchBase.Mui-checked": {
                          color: White,
                        },
                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                          {
                            backgroundColor: NeonGreen,
                            opacity: 1,
                          },
                      }}
                    />
                  }
                  label=""
                />
              </RowItem>
            </Card>

            <Button
              width="100%"
              className="mb-50"
              disabled={loading || saving}
              $background={DarkGrey}
              style={{ fontSize: "15px" }}
            >
              {saving
                ? "Saving..."
                : loading
                ? "Loading..."
                : "Preferences Saved"}
            </Button>
          </Col>
        </Row>
      </Container>
    </Layout>
  );
};

export default SettingsNotifications;
